/*
 * Counterpoint free bar, 9 Oct 2026 (GH Produce, Broadcast 8.1.1).
 *
 * At $50 of qualifying items the cart gets one free bar line (property _free_bar),
 * and loses it again under $50. The theme never sets a price: the Shopify automatic
 * discount does. Not loaded in wholesale sessions (head.liquid gate).
 *
 * Qualifying subtotal, identical to snippets/cp-free-bar-config.liquid: one time lines
 * only; every line of the free bar variant, gift cards and Subscribe & Save lines excluded.
 *
 * Every cart change, however it happens, runs one serialised, idempotent reconcile:
 * read /cart.js, decide, make at most the changes needed, re-read, redraw bar 1.
 * Triggers (August lesson, trap 5): a recompute at load, the theme's cart events,
 * pub/sub cartUpdate, and a debounced MutationObserver on the cart's swap holders.
 */
(function () {
  'use strict';

  if (window.CpFreeBar) return;
  var cfg = window.CpFreeBarConfig;
  if (!cfg || !cfg.variantId) return;

  var DECLINED_KEY = 'cp_free_bar_declined';
  var DEBOUNCE_MS = 150;

  var running = null;
  var again = false;
  var timer = null;
  var addRefused = false; // the cart refused the variant (422, e.g. stock): no automatic retry on this page
  var addFailed = false; // the last add did not land (rate limit, network): retried on the next cart change

  function store(key, value) {
    try {
      if (value === null) window.sessionStorage.removeItem(key);
      else window.sessionStorage.setItem(key, value);
    } catch (e) { /* storage blocked: the offer still works, a removal is just not remembered */ }
  }
  function read(key) {
    try { return window.sessionStorage.getItem(key); } catch (e) { return null; }
  }

  function routes() {
    return (window.theme && window.theme.routes) || {};
  }

  function money(cents) {
    if (window.theme && window.theme.formatMoney && window.theme.moneyFormat) {
      return window.theme.formatMoney(cents, window.theme.moneyFormat);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function request(path, body) {
    var options = {headers: {'Content-Type': 'application/json', Accept: 'application/json'}};
    if (body) {
      options.method = 'POST';
      options.body = JSON.stringify(body);
    }
    return fetch(path, options).then(function (response) {
      return response.json().then(function (json) {
        return {ok: response.ok, status: response.status, json: json};
      }, function () {
        return {ok: false, status: response.status, json: null};
      });
    });
  }

  function getCart() {
    return request((routes().cart_url || '/cart') + '.js').then(function (r) {
      return r.json;
    });
  }

  function isFreeLine(item) {
    return !!(item.properties && item.properties._free_bar);
  }

  function analyse(cart) {
    var qualifying = 0;
    var freeLines = [];
    (cart.items || []).forEach(function (item) {
      if (isFreeLine(item)) freeLines.push(item);
      if (item.selling_plan_allocation || item.gift_card || item.variant_id === cfg.variantId) return;
      qualifying += item.final_line_price;
    });
    return {qualifying: qualifying, freeLines: freeLines, qualified: qualifying >= cfg.threshold};
  }

  function declined() {
    return read(DECLINED_KEY) === '1';
  }

  function change(key, quantity) {
    return request((routes().cart_change_url || '/cart/change') + '.js', {id: key, quantity: quantity});
  }

  function add() {
    return request((routes().cart_add_url || '/cart/add') + '.js', {
      items: [{id: cfg.variantId, quantity: 1, properties: {_free_bar: 'true'}}],
    }).then(function (r) {
      addFailed = !r.ok;
      if (r.status === 422) addRefused = true;
      return r.ok;
    }, function () {
      addFailed = true;
      return false;
    });
  }

  /* One pass. Returns true if it changed the cart. */
  function step() {
    return getCart().then(function (cart) {
      var info = analyse(cart);
      var work = [];
      if (!info.qualified) {
        info.freeLines.forEach(function (line) {
          work.push(function () { return change(line.key, 0); });
        });
      } else if (info.freeLines.length) {
        info.freeLines.forEach(function (line, i) {
          if (i > 0) work.push(function () { return change(line.key, 0); });
          else if (line.quantity !== 1) work.push(function () { return change(line.key, 1); });
        });
      } else if (!declined() && cfg.available && !addRefused) {
        work.push(add);
      }

      if (!work.length) return {changed: false, cart: cart, info: info};

      return work.reduce(function (p, fn) { return p.then(fn); }, Promise.resolve()).then(function () {
        return getCart().then(function (fresh) {
          return {changed: true, cart: fresh, info: analyse(fresh)};
        });
      });
    }).then(function (result) {
      render(result.info);
      if (result.changed) {
        // Re-render the theme's drawer or cart page from the server, exactly as the theme does.
        document.dispatchEvent(new CustomEvent('theme:cart:refresh', {bubbles: true}));
      }
      return result.changed;
    });
  }

  /* Serialised: one pass at a time; a trigger during a pass queues exactly one more pass. */
  function reconcile() {
    if (running) {
      again = true;
      return running;
    }
    running = (function loop() {
      again = false;
      return step().catch(function () { return false; }).then(function () {
        if (again) return loop();
      });
    })().then(function () {
      running = null;
    });
    return running;
  }

  function schedule() {
    if (timer) clearTimeout(timer);
    timer = setTimeout(function () {
      timer = null;
      reconcile();
    }, DEBOUNCE_MS);
  }

  function settled() {
    if (timer) {
      clearTimeout(timer);
      timer = null;
      return reconcile();
    }
    return running || Promise.resolve();
  }

  function render(info) {
    var nodes = document.querySelectorAll('[data-cp-fb-progress]');
    if (!nodes.length) return;
    var isDeclined = declined();
    var percent = Math.min(100, Math.floor((info.qualifying * 100) / cfg.threshold));
    var text;
    var showReadd = false;
    if (!info.qualified) {
      text = 'Spend ' + money(cfg.threshold - info.qualifying) + ' more for a free ' + cfg.title;
    } else if (!info.freeLines.length && (isDeclined || addFailed || addRefused)) {
      text = "You've earned a free " + cfg.title + '.';
      showReadd = true;
    } else {
      text = 'Free ' + cfg.title + ' added to your order';
    }
    nodes.forEach(function (node) {
      node.hidden = !cfg.available;
      var message = node.querySelector('[data-cp-fb-message]');
      var meter = node.querySelector('[data-cp-fb-meter]');
      var readd = node.querySelector('[data-cp-fb-readd]');
      if (message && message.textContent !== text) message.textContent = text;
      if (meter) meter.value = percent;
      if (readd) readd.hidden = !showReadd;
    });
  }

  // The customer removed the free bar: its own Remove control is the only signal (audit R1, 9 Oct 2026).
  // Do not add it back in this browser session.
  document.addEventListener('click', function (event) {
    var remove = event.target.closest && event.target.closest('.cart__item--cp-free-bar [data-item-remove]');
    if (remove) store(DECLINED_KEY, '1');
  }, true);

  // "Add it to my order"
  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-cp-fb-readd]');
    if (!button) return;
    event.preventDefault();
    store(DECLINED_KEY, null);
    addRefused = false;
    addFailed = false;
    button.disabled = true;
    reconcile().then(function () { button.disabled = false; });
  });

  // Hold Check out until an add or remove in flight has settled, then let it go through.
  var releasing = false;
  var CHECKOUT = 'button[name="checkout"], input[name="checkout"], a[href*="/checkout"]';
  function checkoutTarget(el) {
    return (el && el.closest && el.closest(CHECKOUT)) || null;
  }
  function busy() {
    return !!(running || timer);
  }
  document.addEventListener('click', function (event) {
    var target = checkoutTarget(event.target);
    if (releasing || !busy() || !target) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    settled().then(function () { return running; }).then(function () {
      releasing = true;
      target.click();
      releasing = false;
    });
  }, true);
  document.addEventListener('submit', function (event) {
    var submitter = event.submitter;
    if (releasing || !busy() || !checkoutTarget(submitter)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    settled().then(function () { return running; }).then(function () {
      releasing = true;
      submitter.click();
      releasing = false;
    });
  }, true);

  ['theme:cart:change', 'theme:cart:refresh', 'theme:product:add', 'theme:product:added', 'theme:cart-drawer:open', 'shopify:section:load', 'pageshow'].forEach(function (name) {
    (name === 'pageshow' ? window : document).addEventListener(name, schedule);
  });

  function subscribeCartUpdate() {
    if (window.subscribe && window.theme && window.theme.PUB_SUB_EVENTS && window.theme.PUB_SUB_EVENTS.cartUpdate) {
      try { window.subscribe(window.theme.PUB_SUB_EVENTS.cartUpdate, schedule); } catch (e) { /* observers below still cover it */ }
    }
  }

  function observe() {
    if (!window.MutationObserver) return;
    var targets = document.querySelectorAll('[data-items-holder], [data-cart-price-holder]');
    if (!targets.length) return;
    var observer = new MutationObserver(schedule);
    targets.forEach(function (target) {
      observer.observe(target, {childList: true, subtree: true});
    });
  }

  var started = false;
  function init() {
    if (started) return;
    started = true;
    subscribeCartUpdate();
    observe();
    reconcile();
  }

  window.CpFreeBar = {reconcile: reconcile, settled: settled, analyse: analyse};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
